import clsx from 'clsx'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import CircularButton from '@/components-latest/primitives/CircularButton'
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter
} from '@/components-latest/primitives/Drawer'
import { Cross, CashPayment } from '@/components-latest/primitives/Icons'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import OrderStatus from '@/components-latest/ui/OrdersPage/OrderStatus/OrderStatus'
import { SupportedLanguages } from '@/config/i18n'
import {
  WalletTransactionDetailsPaymentMethod,
  WalletTransactionDetailsViewModel
} from '@/ducks/billing/billing.types'
import { OrderStatuses } from '@/ducks/orders/orders.types'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './TransactionDetailsDrawer.module.scss'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  details?: WalletTransactionDetailsViewModel
  isLoading?: boolean
  isError?: boolean
  formatDateTime: (value: string) => string
}

type WalletTransactionStatus = 'pending' | 'done' | 'refund'

const mapWalletTransactionStatus = (
  status: string | undefined
): WalletTransactionStatus => {
  const normalizedStatus = String(status || '').toUpperCase()

  if (normalizedStatus === 'CONFIRMED' || normalizedStatus === 'DONE') {
    return 'done'
  }
  if (normalizedStatus === 'REFUND' || normalizedStatus === 'REFUNDED') {
    return 'refund'
  }

  return 'pending'
}

const isCashPaymentMethod = (
  paymentMethod?: WalletTransactionDetailsPaymentMethod
) => {
  if (!paymentMethod) return false

  const labels = [
    paymentMethod.displayName,
    ...Object.values(paymentMethod.displayNameI18n || {})
  ]
    .filter(Boolean)
    .map(label => label.toUpperCase())

  if (
    labels.some(
      label =>
        label === 'CASH' ||
        label.includes('CASH') ||
        label.includes('ESPÈCES') ||
        label.includes('ESPECES')
    )
  ) {
    return true
  }

  return Boolean(paymentMethod.icon?.includes('money_cash'))
}

const mapWalletTransactionType = (transactionType: string | undefined) => {
  const normalizedType = String(transactionType || '').toUpperCase()
  if (normalizedType.includes('TOP_UP')) return 'Top up'
  if (normalizedType.includes('CASH_OUT')) return 'Cash out'
  if (normalizedType.includes('ORDER')) return 'Order'
  return transactionType || '-'
}

const DetailRow = ({
  label,
  value,
  valueClassName,
  valueNode,
  alignValueStart = false
}: {
  label: string
  value?: string
  valueClassName?: string
  valueNode?: React.ReactNode
  alignValueStart?: boolean
}) => (
  <div
    className={clsx(Styles.summaryRow, {
      [Styles.summaryRowValueStart]: alignValueStart
    })}>
    <Text variant="nextSubtitle">{label}</Text>
    {!alignValueStart ? <div className={Styles.dots} /> : null}
    <div
      className={clsx({
        [Styles.summaryValueStart]: alignValueStart
      })}>
      {valueNode || (
        <Text
          variant="nextSubtitle"
          weight="bold"
          className={clsx(valueClassName)}>
          {value}
        </Text>
      )}
    </div>
  </div>
)

const WalletTransactionStatusBadge = ({
  status,
  label
}: {
  status: WalletTransactionStatus
  label: string
}) => (
  <span
    className={clsx(Styles.walletStatusTag, {
      [Styles.walletStatusPending]: status === 'pending',
      [Styles.walletStatusDone]: status === 'done',
      [Styles.walletStatusRefund]: status === 'refund'
    })}>
    <span
      className={clsx(Styles.walletStatusDot, {
        [Styles.walletStatusDotPending]: status === 'pending',
        [Styles.walletStatusDotDone]: status === 'done',
        [Styles.walletStatusDotRefund]: status === 'refund'
      })}
    />
    {label}
  </span>
)

const TransactionDetailsDrawer = ({
  open,
  onOpenChange,
  details,
  isLoading = false,
  isError = false,
  formatDateTime
}: Props) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as SupportedLanguages

  const paymentMethodLabel = useMemo(() => {
    const paymentMethod = details?.breakdown?.paymentMethod
    if (!paymentMethod) return '-'

    return (
      paymentMethod.displayNameI18n?.[currentLanguage] ||
      paymentMethod.displayName ||
      '-'
    )
  }, [currentLanguage, details?.breakdown?.paymentMethod])

  const orderStatusLabel = useMemo(() => {
    const status = details?.order?.status
    if (!status) return ''

    return status.titleI18n?.[currentLanguage] || status.title
  }, [currentLanguage, details?.order?.status])

  const walletTransactionStatus = mapWalletTransactionStatus(
    details?.transaction?.status
  )
  const walletTransactionStatusLabel = t(
    `billing.wallet.status.${walletTransactionStatus}`
  )

  const currency = details?.transaction?.currency || ''
  const hasOrderBreakdown = Boolean(details?.order && details?.breakdown)
  const formattedDateTime = details?.transaction
    ? formatDateTime(details.transaction.transactionDateTime)
    : ''

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className={Styles.drawer}
        data-testid="wallet-transaction-details-drawer">
        <div className={Styles.topBar}>
          <DrawerClose
            asChild
            data-testid="wallet-transaction-details-close-icon">
            <CircularButton
              variant="flat"
              size="normal"
              aria-label={t('billing.wallet.details.close')}>
              <Cross />
            </CircularButton>
          </DrawerClose>
        </div>

        <ScrollArea>
          <DrawerBody className={Styles.body} data-vaul-no-drag>
            {isLoading ? (
              <div className={Styles.loadingWrapper}>
                <Spinner />
              </div>
            ) : isError ? (
              <div className={Styles.errorWrapper}>
                <Text variant="nextBody" align="center">
                  {t('billing.wallet.details.load-error')}
                </Text>
              </div>
            ) : details ? (
              <>
                <div className={Styles.orderHeaderRow}>
                  {details.order ? (
                    <Text variant="nextBody" weight="semiBold">
                      <span className={Styles.metaText}>
                        {t('billing.wallet.table.order-id')}:{' '}
                      </span>
                      <span className={Styles.orderId}>
                        {details.order.orderId}
                      </span>
                    </Text>
                  ) : (
                    <span />
                  )}
                  {details.order ? (
                    <WalletTransactionStatusBadge
                      status={walletTransactionStatus}
                      label={walletTransactionStatusLabel}
                    />
                  ) : null}
                </div>

                <div className={Styles.section}>
                  <Text
                    variant="nextTitle-2"
                    weight="semiBold"
                    className={Styles.sectionTitle}>
                    {t('billing.wallet.details.general-details-title')}
                  </Text>

                  <DetailRow
                    label={t('billing.wallet.details.transaction-type')}
                    value={mapWalletTransactionType(
                      details.transaction.transactionType
                    )}
                  />

                  {details.order?.status?.key ? (
                    <DetailRow
                      label={t('billing.wallet.details.order-status')}
                      valueNode={
                        <OrderStatus
                          assigned
                          status={
                            details.order?.status?.key as OrderStatuses
                          }>
                          {orderStatusLabel}
                        </OrderStatus>
                      }
                    />
                  ) : null}

                  {details.order?.customerName ? (
                    <DetailRow
                      label={t('billing.wallet.details.client-name')}
                      value={details.order.customerName}
                    />
                  ) : null}

                  <DetailRow
                    label={t('billing.wallet.table.date-time')}
                    value={formattedDateTime}
                  />
                </div>

                <div className={Styles.sectionDivider} />

                {hasOrderBreakdown && details.breakdown ? (
                  <div className={Styles.section}>
                    <Text
                      variant="nextTitle-2"
                      weight="semiBold"
                      className={Styles.sectionTitle}>
                      {t('billing.wallet.details.billing-details-title')}
                    </Text>

                    <DetailRow
                      label={t('billing.wallet.details.order-amount')}
                      value={formatPrice(details.breakdown.subtotal, currency, {
                        signDisplay: 'never'
                      })}
                    />
                    <DetailRow
                      label={t('billing.wallet.details.yassir-commission')}
                      value={formatPrice(
                        -Math.abs(details.breakdown.commission),
                        currency,
                        { signDisplay: 'never' }
                      )}
                      valueClassName={Styles.negative}
                    />
                    <DetailRow
                      label={t('orders.discount')}
                      value={formatPrice(
                        -Math.abs(details.breakdown.discount),
                        currency,
                        { signDisplay: 'never' }
                      )}
                      valueClassName={Styles.negative}
                    />
                    <DetailRow
                      label={t('orders.payment-method')}
                      valueNode={
                        <span className={Styles.paymentMethodChip}>
                          {isCashPaymentMethod(
                            details.breakdown.paymentMethod
                          ) ? (
                            <CashPayment className={Styles.paymentMethodIconSvg} />
                          ) : details.breakdown.paymentMethod.icon ? (
                            <img
                              src={details.breakdown.paymentMethod.icon}
                              alt=""
                              className={Styles.paymentMethodIcon}
                            />
                          ) : null}
                          <span className={Styles.paymentMethodLabel}>
                            {paymentMethodLabel}
                          </span>
                        </span>
                      }
                    />
                    <div className={clsx(Styles.summaryRow, Styles.totalRow)}>
                      <Text variant="nextSubtitle" weight="semiBold">
                        {t('billing.wallet.details.total')}
                      </Text>
                      <div className={Styles.dots} />
                      <Text
                        variant="heading-05"
                        weight="bold"
                        className={Styles.positive}>
                        {formatPrice(details.breakdown.earnings, currency, {
                          signDisplay: 'never'
                        })}
                      </Text>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </DrawerBody>
        </ScrollArea>

        <DrawerFooter className={Styles.footer}>
          <DrawerClose asChild>
            <Button
              variant="branded"
              size="large"
              shape="rounded"
              className={Styles.closeButton}>
              {t('billing.wallet.details.close')}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default TransactionDetailsDrawer
